import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { T } from "@/components/T";
import Seo from "@/components/Seo";
import {
  proMe, proBookings, proBookingDetail, proBookingCharts, proRecommend,
} from "@/lib/api";
import {
  ShieldCheck, Clock, XCircle, ChevronLeft, Sparkles, Star, Loader2,
  CalendarClock, MapPin, User, Lock, Send, Moon,
} from "lucide-react";

const inp = "w-full border border-line bg-surface px-3 py-2.5 text-sm text-ink focus:border-saffron focus:outline-none";
const PLANET_ORDER = ["Sun", "Moon", "Mars", "Mercury", "Jupiter", "Venus", "Saturn", "Rahu", "Ketu"];

const fmtDate = (s) => {
  try { return new Date(s).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return s || "—"; }
};
const fmtUnix = (sec) => {
  try { return new Date(sec * 1000).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

function Gate({ icon: Icon, color, children }) {
  return (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center" data-testid="pro-portal-gate">
      <Icon className={`mx-auto h-10 w-10 ${color}`} />
      <p className="mt-4 text-ink-2">{children}</p>
    </div>
  );
}

// ---- Chart display (engine-verbatim, read-only) ----
function ChartTables({ data }) {
  const chart = data?.chart;
  const lagna = chart?.lagna;
  const planets = chart?.planets || {};
  const cur = data?.dasha?.current;
  if (!chart) return null;
  return (
    <div className="mt-5 space-y-5" data-testid="pro-charts">
      <div className="border border-line bg-surface p-4">
        <h4 className="font-serif-latin text-base text-ink"><T>Lagna (Ascendant)</T></h4>
        {lagna && (
          <p className="mt-1 text-sm text-ink-2" data-testid="pro-lagna">
            <span className="font-medium text-ink">{lagna.rashi}</span> · {lagna.dms} ·{" "}
            <T>Nakshatra</T> {lagna.nakshatra} ({lagna.nakshatra_lord}) · <T>Pada</T> {lagna.pada}
          </p>
        )}
      </div>

      <div className="border border-line bg-surface p-4">
        <h4 className="font-serif-latin text-base text-ink"><T>Planetary Placements</T></h4>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="pro-planets-table">
            <thead>
              <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-3">
                <th className="py-2 pr-3"><T>Graha</T></th>
                <th className="py-2 pr-3"><T>Rashi</T></th>
                <th className="py-2 pr-3"><T>House</T></th>
                <th className="py-2 pr-3"><T>Degree</T></th>
                <th className="py-2 pr-3"><T>Nakshatra</T></th>
              </tr>
            </thead>
            <tbody>
              {PLANET_ORDER.filter((p) => planets[p]).map((p) => {
                const d = planets[p];
                return (
                  <tr key={p} className="border-b border-line/60" data-testid={`pro-planet-${p.toLowerCase()}`}>
                    <td className="py-2 pr-3 font-medium text-ink">
                      {p}{d.retrograde ? <span className="ml-1 text-xs text-saffron">{"(R)"}</span> : null}
                    </td>
                    <td className="py-2 pr-3 text-ink-2">{d.rashi}</td>
                    <td className="py-2 pr-3 text-ink-2">{d.house}</td>
                    <td className="py-2 pr-3 text-ink-2">{d.dms}</td>
                    <td className="py-2 pr-3 text-ink-2">{d.nakshatra} ({d.nakshatra_lord})</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {cur && (
        <div className="border border-line bg-surface p-4" data-testid="pro-dasha">
          <h4 className="font-serif-latin text-base text-ink"><T>Current Vimshottari Dasha</T></h4>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {[["Mahadasha", cur.mahadasha], ["Antardasha", cur.antardasha], ["Pratyantar", cur.pratyantar]].map(([label, v]) => v && (
              <div key={label} className="border border-line/60 p-3">
                <p className="text-xs uppercase tracking-wider text-ink-3"><T>{label}</T></p>
                <p className="mt-0.5 font-medium text-ink">{v.lord}</p>
                <p className="text-xs text-ink-3">{fmtUnix(v.start)} – {fmtUnix(v.end)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Recommendation editor ----
function RecForm({ booking, onSaved }) {
  const existing = booking.recommendation;
  const [f, setF] = useState({
    title: existing?.title || "",
    summary: existing?.summary || "",
    remedies: (existing?.remedies || []).join("\n"),
    mantras: (existing?.mantras || []).join("\n"),
    follow_up: existing?.follow_up || "",
    private_notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!f.title.trim() && !f.summary.trim()) return toast.error("Add a title or summary first");
    setBusy(true);
    try {
      const payload = {
        title: f.title, summary: f.summary, follow_up: f.follow_up, private_notes: f.private_notes,
        remedies: f.remedies.split("\n").map((x) => x.trim()).filter(Boolean),
        mantras: f.mantras.split("\n").map((x) => x.trim()).filter(Boolean),
      };
      const r = await proRecommend(booking.booking.booking_id, payload);
      toast.success("Recommendation sent to the seeker");
      onSaved(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  return (
    <div className="mt-6 border border-line bg-surface p-5" data-testid="pro-rec-form">
      <h3 className="flex items-center gap-2 font-serif-latin text-lg text-ink">
        <Sparkles className="h-4 w-4 text-saffron" /><T>Write your recommendation</T>
      </h3>
      <div className="mt-4 space-y-3">
        <input className={inp} placeholder="Title (e.g. Strengthen Jupiter)" data-testid="pro-rec-title"
          value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <textarea className={`${inp} min-h-[90px]`} placeholder="Summary / guidance for the seeker" data-testid="pro-rec-summary"
          value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} />
        <div className="grid gap-3 sm:grid-cols-2">
          <textarea className={`${inp} min-h-[80px]`} placeholder="Remedies — one per line" data-testid="pro-rec-remedies"
            value={f.remedies} onChange={(e) => setF({ ...f, remedies: e.target.value })} />
          <textarea className={`${inp} min-h-[80px]`} placeholder="Mantras — one per line" data-testid="pro-rec-mantras"
            value={f.mantras} onChange={(e) => setF({ ...f, mantras: e.target.value })} />
        </div>
        <input className={inp} placeholder="Follow-up note (optional)" data-testid="pro-rec-followup"
          value={f.follow_up} onChange={(e) => setF({ ...f, follow_up: e.target.value })} />
        <textarea className={`${inp} min-h-[60px]`} placeholder="Private notes (only you can see these)" data-testid="pro-rec-private"
          value={f.private_notes} onChange={(e) => setF({ ...f, private_notes: e.target.value })} />
        <button onClick={submit} disabled={busy} data-testid="pro-rec-submit"
          className="inline-flex items-center gap-2 bg-saffron px-5 py-2.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {existing ? <T>Update recommendation</T> : <T>Send recommendation</T>}
        </button>
      </div>
    </div>
  );
}

// ---- Booking detail view ----
function BookingDetail({ bookingId, onBack }) {
  const [detail, setDetail] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(false);

  useEffect(() => {
    proBookingDetail(bookingId).then(setDetail).catch((e) => toast.error(e?.response?.data?.detail || "Could not load"));
  }, [bookingId]);

  const loadCharts = async () => {
    setLoadingCharts(true);
    try { setCharts(await proBookingCharts(bookingId)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Charts unavailable"); }
    setLoadingCharts(false);
  };

  if (!detail) return <div className="py-16 text-center text-ink-3">…</div>;
  const b = detail.booking;
  const sb = detail.shared_birth;

  return (
    <div data-testid="pro-booking-detail">
      <button onClick={onBack} className="mb-5 inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink" data-testid="pro-back-btn">
        <ChevronLeft className="h-4 w-4" /><T>All consultations</T>
      </button>

      <div className="border border-line bg-surface p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-serif-latin text-xl text-ink">{b.service_name || b.booking_type}</h2>
            <p className="mt-1 text-sm text-ink-3 capitalize">{b.booking_type} · {b.booking_id}</p>
          </div>
          <span className="shrink-0 border border-line px-3 py-1 text-xs uppercase tracking-wider text-ink-2">{b.status}</span>
        </div>
        <div className="mt-4 grid gap-2 text-sm text-ink-2 sm:grid-cols-2">
          <p className="flex items-center gap-2"><User className="h-4 w-4 text-ink-3" />{b.name || "—"}</p>
          <p className="flex items-center gap-2"><CalendarClock className="h-4 w-4 text-ink-3" />{b.preferred_date || "Date to confirm"}</p>
          {b.city && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-ink-3" />{b.city}</p>}
        </div>
        {b.message && <p className="mt-3 border-l-2 border-saffron/50 pl-3 text-sm text-ink-2">{b.message}</p>}
      </div>

      {/* Birth details */}
      <div className="mt-5 border border-line bg-surface p-5" data-testid="pro-birth-block">
        <h3 className="font-serif-latin text-lg text-ink"><T>Birth details</T></h3>
        {detail.has_shared_birth ? (
          <>
            <p className="mt-2 text-sm text-ink-2" data-testid="pro-birth-shared">
              <span className="font-medium text-ink">{sb.name || "Seeker"}</span> · {fmtDate(sb.dob)} · {sb.tob || "time n/a"} · {sb.place || "—"}
            </p>
            {!charts ? (
              <button onClick={loadCharts} disabled={loadingCharts} data-testid="pro-load-charts-btn"
                className="mt-3 inline-flex items-center gap-2 border border-saffron px-4 py-2 text-sm text-saffron hover:bg-saffron/5 disabled:opacity-50">
                {loadingCharts ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />}
                <T>Load birth charts</T>
              </button>
            ) : <ChartTables data={charts} />}
          </>
        ) : (
          <div className="mt-2 flex items-start gap-2 text-sm text-ink-3" data-testid="pro-birth-locked">
            <Lock className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              <T>The seeker has not shared their saved birth chart yet.</T>
              {(b.birth_date || b.birth_place) && (
                <span className="mt-2 block text-ink-2">
                  <T>From the booking form:</T> {b.birth_date || "—"} {b.birth_time ? `· ${b.birth_time}` : ""} {b.birth_place ? `· ${b.birth_place}` : ""}
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      <RecForm booking={detail} onSaved={(r) => setDetail((d) => ({ ...d, recommendation: r }))} />
    </div>
  );
}

export default function ProPortal() {
  const { user, ready } = useAuth();
  const [profile, setProfile] = useState(undefined);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  const loadBookings = useCallback(() => {
    proBookings().then((d) => setData(d)).catch(() => setData({ bookings: [] }));
  }, []);

  useEffect(() => {
    if (!user) return;
    proMe().then((d) => {
      setProfile(d.profile || null);
      if (d.profile?.verification_status === "verified") loadBookings();
    }).catch(() => setProfile(null));
  }, [user, loadBookings]);

  if (ready && !user) return <Navigate to="/login" replace />;
  if (profile === undefined) return <div className="py-24 text-center text-ink-3">…</div>;

  if (!profile) return (
    <Gate icon={XCircle} color="text-ink-3">
      <T>This portal is for verified Dharmartha professionals. Apply first on the Join as Purohit page.</T>
    </Gate>
  );
  if (profile.verification_status !== "verified") return (
    <Gate icon={Clock} color="text-amber-600">
      <T>Your professional profile is under review. The portal opens once you are verified.</T>
    </Gate>
  );

  return (
    <div className="mx-auto max-w-4xl px-5 py-14 md:px-8">
      <Seo title="Professional Portal — Dharmartha" noindex />
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-green-600" />
        <span className="text-xs uppercase tracking-wider text-green-700"><T>Verified</T> · {profile.type}</span>
        {profile.aadhaar_verified && (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2.5 py-1 text-xs font-medium text-green-700" data-testid="pro-aadhaar-verified-badge">
            <ShieldCheck className="h-3.5 w-3.5" /><T>Aadhaar verified</T>
          </span>
        )}
      </div>
      <h1 className="mt-2 font-serif-latin text-3xl text-ink">{profile.name}</h1>
      <p className="mt-1 text-sm text-ink-3"><T>Your assigned consultations, birth charts and recommendations.</T></p>

      <div className="mt-10">
        {selected ? (
          <BookingDetail bookingId={selected} onBack={() => { setSelected(null); loadBookings(); }} />
        ) : !data ? (
          <p className="py-16 text-center text-ink-3">…</p>
        ) : data.bookings.length === 0 ? (
          <div className="border border-line bg-surface py-16 text-center" data-testid="pro-no-bookings">
            <Moon className="mx-auto h-8 w-8 text-ink-3" />
            <p className="mt-3 text-ink-3"><T>No consultations assigned to you yet.</T></p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="pro-bookings-list">
            {data.bookings.map((b) => (
              <button key={b.booking_id} onClick={() => setSelected(b.booking_id)} data-testid={`pro-booking-${b.booking_id}`}
                className="flex w-full items-center justify-between gap-4 border border-line bg-surface p-5 text-left transition-colors hover:border-saffron">
                <div>
                  <p className="font-serif-latin text-lg text-ink">{b.service_name || b.booking_type}</p>
                  <p className="text-sm text-ink-3 capitalize">{b.name} · {b.booking_type} · {b.preferred_date || b.birth_date || "date to confirm"}</p>
                </div>
                <span className="shrink-0 border border-line px-3 py-1 text-xs uppercase tracking-wider text-ink-2">{b.status}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
